import { useEffect, useRef, useState, type FormEvent } from 'react';
import { formatDate } from '../util/datetime';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';

import * as usersApi from '../api/users';
import type { UserUpdate } from '../api/users';
import * as npiApi from '../api/npi';
import { useAuth, userHasRole, userHoldsExactRole } from '../auth/AuthContext';
import { isProfileComplete } from '../auth/profileComplete';
import { TotpProfileSection } from '../components/TotpProfileSection';
import { ChangePasswordSection } from '../components/ChangePasswordSection';
import {
  filterPhoneInput,
  PHONE_LIKE_FIELDS,
  userProfileValidators,
  type UserProfileField,
} from '../validation/profile';
import {
  asCountryCode,
  formatPhoneAsYouType,
  getCountryCallingCodePrefix,
  getPhonePlaceholder,
  validatePhone,
} from '../validation/phone';
import { findCountry, SUPPORTED_COUNTRIES } from '../lookup/countries';

// Country picker options, alphabetical by the name shown.
const COUNTRY_NAMES = SUPPORTED_COUNTRIES.map((c) => c.name).sort((a, b) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' }),
);
import { usePostalLookup } from '../lookup/usePostalLookup';

/**
 * Profile page — `/me` view + edit.
 *
 * Three cards:
 *   - Account: read-only email + verification status + member-since
 *   - Profile: editable full name + contact + address (the only
 *     fields a user owns directly; email + password go through
 *     dedicated flows).
 *   - Roles: memberships across tenants.
 *
 * Validation strategy: each formatted field (phone/mobile/city/state/
 * postal/country) runs a regex check on blur. The error message lives
 * in component state and is rendered via MUI's `error` + `helperText`
 * props. The Save button is disabled while any error is present so
 * the user can't submit a known-bad value. The server runs the same
 * regexes (`api/app/schemas/validation.py`) — we never trust client
 * validation alone.
 */
type ProfileFormState = {
  full_name: string;
  npi: string;
  // `title` is free text (e.g. "Doctor", "Professor"). Kept as a
  // plain string in the form even though the server allows null;
  // empty string → server clears the column. Required only when the
  // user holds a participant role (gate enforced below).
  title: string;
  // Kept as a string in the form so an empty input stays an empty
  // string rather than NaN; coerced to number (or null) at submit.
  years_of_experience: string;
  phone: string;
  mobile: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state_region: string;
  postal_code: string;
  country: string;
};

const emptyForm: ProfileFormState = {
  full_name: '',
  npi: '',
  title: '',
  years_of_experience: '',
  phone: '',
  mobile: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state_region: '',
  postal_code: '',
  country: '',
};

function userToForm(u: usersApi.CurrentUser | null): ProfileFormState {
  if (!u) return emptyForm;
  return {
    full_name: u.full_name ?? '',
    npi: u.npi ?? '',
    title: u.title ?? '',
    years_of_experience:
      u.years_of_experience === null ? '' : String(u.years_of_experience),
    phone: u.phone ?? '',
    mobile: u.mobile ?? '',
    address_line1: u.address_line1 ?? '',
    address_line2: u.address_line2 ?? '',
    city: u.city ?? '',
    state_region: u.state_region ?? '',
    postal_code: u.postal_code ?? '',
    country: u.country ?? '',
  };
}

type ErrorMap = Partial<Record<UserProfileField, string>>;

/** Simple tab panel — unmounts its children when inactive (matches the
 *  TenantSettings pattern). The shared Profile form state lives on the
 *  page, so switching tabs never loses unsaved edits. */
function TabPanel({
  value,
  index,
  children,
}: {
  value: number;
  index: number;
  children: React.ReactNode;
}) {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ mt: 1 }}>
      {children}
    </Box>
  );
}

export function Profile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  // Latch whether the user landed here in onboarding state (profile
  // was incomplete on first render). When they save and the profile
  // flips complete, we bounce them to /dashboard so the welcome
  // flow has a clear end. Subsequent edits don't navigate away —
  // the user is intentionally on /profile in that case.
  const wasIncompleteOnMountRef = useRef<boolean | null>(null);
  if (wasIncompleteOnMountRef.current === null && user) {
    wasIncompleteOnMountRef.current = !isProfileComplete(user);
  }
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<ProfileFormState>(emptyForm);
  const [errors, setErrors] = useState<ErrorMap>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NPI lookup state. The button is enabled when `form.npi` is
  // exactly 10 digits; pressing it calls the backend proxy and
  // overwrites the contact block with whatever the registry
  // returned. The user can still hand-edit afterwards before
  // hitting Save — the autofill is a head-start, not a lock.
  const [npiLoading, setNpiLoading] = useState(false);
  const [npiMessage, setNpiMessage] = useState<{
    severity: 'info' | 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);

  // Postal-code lookup: derive an ISO country code from whatever the
  // user has in the country field, then hand it + the postal code to
  // the hook. The hook debounces + caches; we just react to the result.
  const country = findCountry(form.country);
  const lookup = usePostalLookup(country?.code ?? null, form.postal_code);
  // Computed here (before any conditional return) so the
  // country-change effect below can sit alongside the other hooks
  // and not after the early return — see the rules-of-hooks comment
  // on the third useEffect.
  const countryCode = asCountryCode(country?.code);

  // Postal-code lookup is authoritative: whenever it returns a hit
  // we overwrite city + state, so a misspelled city ("Bostan") gets
  // corrected once the user enters a valid ZIP. Only fires on a new
  // lookup result, so the user can still hand-edit city after the
  // autofill completes without us bouncing them back.
  useEffect(() => {
    if (!lookup.result) return;
    setForm((f) => ({
      ...f,
      city: lookup.result!.city,
      state_region: lookup.result!.state,
    }));
    // Also clear any error flags on the autofilled fields — the data
    // came from the API, so by definition it's valid.
    setErrors((prev) => {
      const next = { ...prev };
      delete next.city;
      delete next.state_region;
      return next;
    });
  }, [lookup.result]);

  useEffect(() => {
    setForm(userToForm(user));
    setErrors({});
  }, [user]);

  // When the user changes country we need to re-validate any phone
  // fields they've already filled in (US digit counts differ from
  // UK, etc.) and also re-format with the new country's rules. This
  // effect must sit ABOVE the `if (!user) return null` early return
  // — moving it below would mean React sees a different hook count
  // before vs. after auth resolves, violating rules-of-hooks.
  useEffect(() => {
    if (!countryCode) return;
    setForm((f) => ({
      ...f,
      phone: f.phone ? formatPhoneAsYouType(f.phone, countryCode) : f.phone,
      mobile: f.mobile ? formatPhoneAsYouType(f.mobile, countryCode) : f.mobile,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const field of ['phone', 'mobile'] as const) {
        // Re-validate using the new country. `form` reference in
        // closure is fine — we only care about whether the *new*
        // value is acceptable; we just set it in the line above.
        const msg = validatePhone(form[field], countryCode);
        if (msg) next[field] = msg;
        else delete next[field];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  if (!user) return null;

  const base = userToForm(user);
  const dirty = (Object.keys(form) as (keyof ProfileFormState)[]).some(
    (k) => form[k] !== base[k],
  );
  const hasErrors = Object.values(errors).some(Boolean);

  // Title + years-of-experience are required at the UI when the user
  // holds a participant role — the server permits nulls so older
  // accounts + non-participant roles aren't blocked. Strict-role
  // check (not the wildcard) so a superadmin who happens to also
  // participate isn't surprised by a required gate; the gate fires
  // only for accounts whose primary role is participant.
  const isParticipant = userHoldsExactRole(user, 'participant');
  const yearsParsed = form.years_of_experience.trim() === ''
    ? null
    : Number(form.years_of_experience);
  const yearsValid =
    yearsParsed === null ||
    (Number.isInteger(yearsParsed) && yearsParsed >= 0 && yearsParsed <= 80);
  const participantFieldsMissing =
    isParticipant &&
    (form.title.trim() === '' ||
      yearsParsed === null ||
      !yearsValid);
  // Full name is mandatory for every role — it's the audit-log
  // actor name + Sessions list display + the kiosk picker's primary
  // line. The Save button gates on this in addition to the
  // participant-only fields above.
  const fullNameMissing = form.full_name.trim() === '';

  const update = (k: keyof ProfileFormState) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const raw = e.target.value;

    // Phone-like fields: strip disallowed chars, then run AsYouType
    // formatting against the selected country. Result is the
    // formatted string we put in state and show in the field — e.g.
    // typing "5551002000" with country=US gives "(555) 100-2000".
    // If no country is set we fall back to the basic char filter so
    // the field still rejects letters.
    let value = raw;
    if (PHONE_LIKE_FIELDS.has(k)) {
      const filtered = filterPhoneInput(raw);
      value = countryCode
        ? formatPhoneAsYouType(filtered, countryCode)
        : filtered;
    }

    setForm((f) => ({ ...f, [k]: value }));

    // Country-aware phone validation when we have both. Otherwise
    // fall through to the regex validator from `userProfileValidators`.
    if (PHONE_LIKE_FIELDS.has(k)) {
      const field = k as UserProfileField;
      const msg = validatePhone(value, countryCode);
      setErrors((prev) => {
        const next = { ...prev };
        if (msg) next[field] = msg;
        else delete next[field];
        return next;
      });
      return;
    }

    if (k in userProfileValidators) {
      const field = k as UserProfileField;
      const msg = userProfileValidators[field](value);
      setErrors((prev) => {
        const next = { ...prev };
        if (msg) next[field] = msg;
        else delete next[field];
        return next;
      });
    }
  };

  const phoneHelperFor = (field: 'phone' | 'mobile'): string => {
    if (errors[field]) return errors[field]!;
    if (countryCode) {
      const prefix = getCountryCallingCodePrefix(countryCode);
      return prefix ? `${countryCode} format · prefix ${prefix}` : ' ';
    }
    return ' ';
  };

  const errorProps = (k: UserProfileField) => ({
    error: Boolean(errors[k]),
    helperText: errors[k] ?? ' ',
  });

  // NPI input filter: digits only, capped at 10. Anything else
  // gets stripped before it lands in state, so the field can never
  // hold a value the backend would 422.
  const updateNpi = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm((f) => ({ ...f, npi: digits }));
    setNpiMessage(null);
  };

  const npiValid = /^\d{10}$/.test(form.npi);

  const handleNpiLookup = async () => {
    if (!npiValid || npiLoading) return;
    setNpiLoading(true);
    setNpiMessage(null);
    try {
      const r = await npiApi.lookupNpi(form.npi);
      // Autofill — overwrite blanks AND already-populated fields.
      // The user can still edit before Save; treating the registry
      // as authoritative (rather than "fill only blanks") is what
      // makes the feature useful for re-syncing a stale profile.
      const phoneFormatted = r.phone && countryCode
        ? formatPhoneAsYouType(filterPhoneInput(r.phone), countryCode)
        : r.phone ?? '';
      setForm((f) => ({
        ...f,
        full_name: r.full_name ?? f.full_name,
        phone: phoneFormatted || f.phone,
        address_line1: r.address_line1 ?? f.address_line1,
        address_line2: r.address_line2 ?? f.address_line2,
        city: r.city ?? f.city,
        state_region: r.state_region ?? f.state_region,
        postal_code: r.postal_code ?? f.postal_code,
        country: r.country ?? f.country,
      }));
      // Whatever we just autofilled is API-blessed, so wipe any
      // stale per-field error flags.
      setErrors({});
      const credentialBit = r.credential ? `, ${r.credential}` : '';
      const taxonomyBit = r.taxonomy_primary ? ` — ${r.taxonomy_primary}` : '';
      setNpiMessage({
        severity: 'success',
        text: `Matched ${r.full_name ?? 'record'}${credentialBit}${taxonomyBit}. Review and Save.`,
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNpiMessage({
          severity: 'warning',
          text: 'No matching provider in the NPI Registry.',
        });
      } else {
        setNpiMessage({
          severity: 'error',
          text:
            (err as { response?: { data?: { detail?: string } } })?.response?.data
              ?.detail ?? 'NPI lookup failed',
        });
      }
    } finally {
      setNpiLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    // Validate every formatted field one more time before submit —
    // covers the "user never blurred a bad field" case.
    const newErrors: ErrorMap = {};
    (Object.keys(userProfileValidators) as UserProfileField[]).forEach((k) => {
      const m = userProfileValidators[k](form[k]);
      if (m) newErrors[k] = m;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    try {
      // Only send fields that actually changed — keeps the PATCH
      // semantics honest and lets the server treat absent keys as
      // "leave alone" while present-but-empty as "clear".
      const body: UserUpdate = {};
      (Object.keys(form) as (keyof ProfileFormState)[]).forEach((k) => {
        if (form[k] === base[k]) return;
        if (k === 'years_of_experience') {
          // Number on the wire (or null when cleared). The form
          // stores it as a string for input-control ergonomics.
          const v = form.years_of_experience.trim();
          body.years_of_experience = v === '' ? null : Number(v);
        } else {
          body[k] = form[k];
        }
      });
      const refreshed = await usersApi.patchMe(body);
      await refresh();
      setSaved(true);
      // If the user landed here via the incomplete-profile guard and
      // their save flipped the profile to complete, take them to the
      // Dashboard so the onboarding flow has an end. Re-running
      // isProfileComplete on the response (not on the stale `user`
      // reference) avoids racing the AuthContext refresh.
      if (
        wasIncompleteOnMountRef.current === true &&
        isProfileComplete(refreshed)
      ) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to save',
      );
    } finally {
      setSaving(false);
    }
  };

  // First-time / incomplete-profile banner. The RequireAuth guard
  // redirects users with an incomplete profile here; the banner
  // explains why they landed on this page rather than the Dashboard
  // and points at the mandatory fields. Once the user saves and the
  // server returns a complete CurrentUser, useAuth's refresh causes
  // isProfileComplete to flip true and the banner disappears.
  const profileIncomplete = !isProfileComplete(user);

  return (
    <Stack spacing={3} sx={{ maxWidth: 880 }}>
      <Typography variant="h4">Profile</Typography>

      {profileIncomplete && (
        <Alert severity="info">
          Welcome to 10NEWTONS! Complete the fields below to continue
          to your dashboard. {isParticipant
            ? 'Full name, Job title, and Years of experience are required for participants.'
            : 'A full name is the only mandatory field — everything else is optional contact info.'}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_e, v: number) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Profile" />
          <Tab label="Account" />
          <Tab label="Security" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Profile
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <TextField
                label="Full name (required)"
                fullWidth
                required
                error={fullNameMissing}
                helperText={fullNameMissing ? 'Required' : ' '}
                value={form.full_name}
                onChange={update('full_name')}
                disabled={saving}
              />

              {/* Professional details — participant-only. Cohort
                  context for trait-score reports; superadmins +
                  tenant_admins aren't asked because the question
                  applies to the person being captured, not the
                  operator running the device. */}
              {isParticipant && (
                <>
                  <Divider sx={{ pt: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Professional details
                    </Typography>
                  </Divider>
                  {participantFieldsMissing && (
                    <Alert severity="info">
                      Participants are required to fill in their job
                      title and years of experience before the profile
                      can be saved.
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Job title (required)"
                        fullWidth
                        value={form.title}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, title: e.target.value }))
                        }
                        disabled={saving}
                        placeholder="e.g. Doctor, Professor, Nurse"
                        required
                        error={form.title.trim() === ''}
                        helperText={
                          form.title.trim() === ''
                            ? 'Required for participants'
                            : ' '
                        }
                        inputProps={{ maxLength: 60 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Years of experience (required)"
                        fullWidth
                        value={form.years_of_experience}
                        onChange={(e) => {
                          // Numeric input — strip anything that isn't
                          // a digit so the field can never hold
                          // something the schema would 422.
                          const digits = e.target.value
                            .replace(/\D/g, '')
                            .slice(0, 2);
                          setForm((f) => ({
                            ...f,
                            years_of_experience: digits,
                          }));
                        }}
                        disabled={saving}
                        required
                        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                        error={
                          form.years_of_experience.trim() === '' || !yearsValid
                        }
                        helperText={
                          !yearsValid
                            ? '0–80'
                            : form.years_of_experience.trim() === ''
                              ? 'Required for participants'
                              : ' '
                        }
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* NPI lookup — participant-only. The CMS Registry is a
                  clinician lookup tool that pre-fills the participant's
                  own contact block; superadmins and tenant_admins
                  manage the platform, not a clinical identity, so it
                  doesn't belong on their profile. */}
              {isParticipant && (
                <>
              <Divider sx={{ pt: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  NPI lookup
                </Typography>
              </Divider>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="NPI (optional)"
                  fullWidth
                  value={form.npi}
                  onChange={updateNpi}
                  disabled={saving || npiLoading}
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                  placeholder="10-digit National Provider Identifier"
                  helperText={
                    form.npi.length === 0
                      ? 'Enter your NPI to pre-fill contact info from the CMS registry.'
                      : npiValid
                        ? ' '
                        : `${form.npi.length}/10 digits`
                  }
                />
                <Button
                  variant="outlined"
                  onClick={handleNpiLookup}
                  disabled={!npiValid || npiLoading || saving}
                  sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  startIcon={npiLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {npiLoading ? 'Looking up…' : 'Look up'}
                </Button>
              </Stack>
              {npiMessage && (
                <Alert severity={npiMessage.severity}>{npiMessage.text}</Alert>
              )}
                </>
              )}

              <Divider sx={{ pt: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Contact
                </Typography>
              </Divider>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Phone"
                    fullWidth
                    value={form.phone}
                    onChange={update('phone')}
                    disabled={saving}
                    error={Boolean(errors.phone)}
                    helperText={phoneHelperFor('phone')}
                    placeholder={getPhonePlaceholder(countryCode)}
                    inputProps={{ inputMode: 'tel' }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Mobile"
                    fullWidth
                    value={form.mobile}
                    onChange={update('mobile')}
                    disabled={saving}
                    error={Boolean(errors.mobile)}
                    helperText={phoneHelperFor('mobile')}
                    placeholder={getPhonePlaceholder(countryCode)}
                    inputProps={{ inputMode: 'tel' }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ pt: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Mailing address
                </Typography>
              </Divider>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    label="Address line 1"
                    fullWidth
                    value={form.address_line1}
                    onChange={update('address_line1')}
                    disabled={saving}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Address line 2"
                    fullWidth
                    value={form.address_line2}
                    onChange={update('address_line2')}
                    disabled={saving}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="City"
                    fullWidth
                    value={form.city}
                    onChange={update('city')}
                    disabled={saving}
                    {...errorProps('city')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="State / region"
                    fullWidth
                    value={form.state_region}
                    onChange={update('state_region')}
                    disabled={saving}
                    {...errorProps('state_region')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Postal code"
                    fullWidth
                    value={form.postal_code}
                    onChange={update('postal_code')}
                    disabled={saving}
                    {...errorProps('postal_code')}
                    InputProps={{
                      // Spinner while lookup runs; checkmark + "City,
                      // State" once we have a hit. Doesn't replace the
                      // input — just decorates the right edge.
                      endAdornment: lookup.loading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : lookup.result ? (
                        <InputAdornment position="end">
                          <Typography
                            variant="caption"
                            color="success.main"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            ✓ {lookup.result.city}, {lookup.result.stateAbbr || lookup.result.state}
                          </Typography>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    freeSolo
                    options={COUNTRY_NAMES}
                    value={form.country}
                    // Selecting from the dropdown fires onChange with
                    // a non-null value; typing freely fires onInputChange
                    // with the raw text. We treat both the same — the
                    // form just stores whatever the user ended up with.
                    onChange={(_, v) => {
                      const value = v ?? '';
                      setForm((f) => ({ ...f, country: value }));
                      const msg = userProfileValidators.country(value);
                      setErrors((prev) => {
                        const next = { ...prev };
                        if (msg) next.country = msg;
                        else delete next.country;
                        return next;
                      });
                    }}
                    onInputChange={(_, v) => {
                      setForm((f) => ({ ...f, country: v }));
                      const msg = userProfileValidators.country(v);
                      setErrors((prev) => {
                        const next = { ...prev };
                        if (msg) next.country = msg;
                        else delete next.country;
                        return next;
                      });
                    }}
                    disabled={saving}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        {...errorProps('country')}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {error && <Alert severity="error">{error}</Alert>}
              {saved && !error && !dirty && (
                <Alert severity="success">Saved.</Alert>
              )}
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    !dirty ||
                    saving ||
                    hasErrors ||
                    fullNameMissing ||
                    participantFieldsMissing
                  }
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Account
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Email
                  </Typography>
                  <Typography>{user.email}</Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Email verified
                  </Typography>
                  {user.email_verified_at ? (
                    <Chip label="Verified" color="success" size="small" />
                  ) : (
                    <Chip label="Pending" color="warning" size="small" />
                  )}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Member since
                  </Typography>
                  <Typography>
                    {formatDate(user.created_at)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Roles
              </Typography>
              <Divider sx={{ my: 1 }} />
              {user.memberships.length === 0 ? (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ mt: 1 }}
                >
                  You don't belong to any tenant yet. An administrator can
                  invite you to one.
                </Typography>
              ) : (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1 }}
                >
                  {user.memberships.map((m, i) => (
                    <Chip
                      key={`${m.role}-${m.tenant_id ?? 'global'}-${i}`}
                      label={
                        m.tenant_id === null
                          ? `${m.role.replace('_', ' ')} · platform`
                          : `${m.role.replace('_', ' ')} · ${m.tenant_name}`
                      }
                      color={m.role === 'superadmin' ? 'primary' : 'default'}
                      variant={
                        m.role === 'superadmin' ? 'filled' : 'outlined'
                      }
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Stack spacing={3}>
          {/* Participants don't see MFA on Profile: enrollment is for
              tenant_admin / superadmin only, voluntary or policy-gated.
              userHasRole wildcards superadmin onto tenant_admin so this
              single check covers both. */}
          {userHasRole(user, 'tenant_admin') && <TotpProfileSection />}
          {/* Every signed-in user can rotate their own password here — the
              in-app path that doesn't depend on email delivery. */}
          <ChangePasswordSection />
        </Stack>
      </TabPanel>
    </Stack>
  );
}
